import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum). 'forbidden': there is no 3D
// geometry to light here at all - one screen-space quad and one Points cloud,
// both lit entirely by their own shaders.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'caustics',
  family: 'organic',
  energyLevel: 0.35,
  bpmMin: 60,
  bpmMax: 135,
  movement: 0.35,
  danceability: 0.30,
  tonality: 0.90,
  // brightness rides artworkPalette.brightness - see orchestrator.js's
  // PREFERS_FIELDS and adaptive-party.js's trackDataInput, where this axis
  // is introduced. It is the only real brightness signal in the payload, and
  // it is the right one here: this is the library's one deliberately luminous
  // visualizer, so a dark cover would give it a water tint it cannot carry.
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.55], brightness: [0.35, 1.0] },
  enabled: true
};

// Caustics: the bright web sunlight draws on the floor of a swimming pool,
// seen from in the water.
//
// The three things that separate a real caustic from a turquoise lava lamp,
// and which every constant below exists to protect:
//
//   1. The lines are THIN and OVEREXPOSED, nearly white. They are not soft
//      cells filled with a gradient.
//   2. They form an IRREGULAR net of curves that cross and close on
//      themselves - never a grid, never rings. This is why each octave gets
//      its own rotation (see OCTAVES): a value-noise lattice is axis-aligned,
//      and three axis-aligned lattices stacked on top of each other read as a
//      weave.
//   3. COLOURED FRINGES at the edges of the lines, because refraction is
//      wavelength-dependent. This is the detail that separates "pretty" from
//      "true".
//
// What is NOT here, deliberately: no director.pulse(). A pool does not pulse.
//
// --- where the light concentrates, and why not |grad h| ----------------------
// The brief specifies a height field h - three drifting octaves of 2D noise -
// and reads the caustic off the places where its GRADIENT vanishes:
//
//     conv = max(1 - |grad h| * uConvergence, 0); conv^8
//
// That was built and rendered first, and ?causticraw=1 on it showed a
// genuinely good net of ribbons. Raised to the eighth power it collapsed into
// scattered specks, and no setting of uConvergence or the exponent fixed it,
// because the problem is not a tuning one. {|grad h| = 0} is the set of
// CRITICAL POINTS of h - maxima, minima and saddles - and that is a set of
// isolated points. Widen the threshold and you get fat blobs around each
// point; sharpen it and you get the points themselves. A network of curves is
// not available anywhere on that dial.
//
// What is actually being drawn has a curve for its answer. A ray refracted by
// the surface lands on the floor displaced by roughly -d * grad h, so the map
// from surface to floor has Jacobian J = I - d*H, where H is the HESSIAN of h
// and d is how far the floor sits below the surface. Light arriving per unit
// area goes as 1/|det J|, and the caustic is the fold: the set where det J
// vanishes, i.e. where a whole patch of surface collapses onto a line. det J
// is one scalar equation in two variables, so its zero set is a curve - and it
// is a curve that closes on itself and crosses its neighbours, which is
// exactly the net the brief describes and the acceptance criteria ask for.
//
// So this file keeps the brief's h, its uConvergence dial (now the focal
// distance d, which is still literally "how much the surface converges", and
// still what bands.mid moves), its eighth power, and its ?causticraw=1 /
// ?expo= debug views. What it replaces is the one line that decides WHERE the
// light is - |grad h| becomes |det(I - d*H)|.
//
// --- and h is never evaluated at all -----------------------------------------
// The naive cost of this is worse, not better: a finite-difference Hessian
// wants six samples of h, times three more for the chromatic offsets, which on
// a three-octave noise is seventy-two hash lookups per pixel. That does not
// run on a Pi.
//
// So h is never materialised. Value noise is a closed-form polynomial in the
// cell fraction, so its second derivatives are available analytically for a
// handful of multiply-adds on top of the same four hash lookups the value
// itself would have needed. ONE evaluation per octave yields the exact
// Hessian - no finite-difference error, and no lattice ringing of the kind
// that would draw the grid criterion 1 forbids. Twelve hash lookups per pixel
// at tier 2, and the dispersion below is then nearly free on top.
//
// See buildFieldChunk for the derivation.

// --- the noise field ---------------------------------------------------------
// The brief's three octaves, verbatim in frequency, amplitude and drift
// velocity. `rot` is this file's own addition: without it every octave shares
// the same axis-aligned lattice and the caustic net picks up a visible
// horizontal/vertical bias - acceptance criterion 1's "never a grid". The
// angles are arbitrary and mutually irrational-ish; nothing depends on their
// exact values beyond being distinct.
export const OCTAVES = [
  { freq: 1.0, amp: 0.50, drift: [0.11, 0.07], rot: 0.00 },
  { freq: 2.3, amp: 0.32, drift: [-0.09, 0.13], rot: 0.86 },
  { freq: 4.7, amp: 0.18, drift: [0.17, -0.05], rot: 1.91 }
];
// Octave count per quality tier. Tier 0 and 1 both drop the finest octave;
// tier 0 additionally drops the chromatic dispersion and the motes.
//
// The tiled floor is NOT tiered - the brief lists it only against tier 2, but
// it is around thirty instructions against the field's two-hundred-odd, and
// without it the caustics light nothing and hang in an empty blue. Dropping it
// would trade the whole subject of the image for a saving too small to
// measure, so every tier keeps it.
const TIER_OCTAVES = [2, 2, 3];
const TIER_DISPERSION = [false, true, true];
const TIER_PARTICLES = [0, 80, 80];
// The particle cloud's own field is cheaper still - it is read 80 times per
// frame total, not once per pixel, but it exists only to correlate with the
// floor's web, and two octaves already carry the structure the eye matches.
const PARTICLE_OCTAVES = 2;

// A fold happens where d * tr(H) reaches 1, so the focal distance d that puts
// folds on screen at all is fixed by the field's typical CURVATURE - which
// goes as amp*freq^2, and which therefore drops by a third when a tier drops
// the finest octave. Without compensation, tier 0 would need a different
// uConvergence than tier 2 to show any net at all. Each tier gets its own
// scale instead, so the same uConvergence means the same thing everywhere and
// what a low tier actually loses is fine detail, not the effect.
function curvatureScale(count) {
  let sum = 0;
  for (let i = 0; i < count; i++) sum += OCTAVES[i].amp * OCTAVES[i].freq * OCTAVES[i].freq;
  return sum;
}
const FULL_CURVATURE = curvatureScale(OCTAVES.length);

// --- geometry of the pool ----------------------------------------------------
// The floor is a plane in perspective, vanishing at HORIZON; above that line
// is the surface seen from underneath. HORIZON at 0.80 is the brief's "upper
// fifth" for the surface band, and putting the floor's vanishing line exactly
// there is what makes the two halves of the image belong to one space.
const HORIZON = 0.80;
// Depth at the bottom edge of the frame is FLOOR_NEAR / HORIZON = 1.25 units.
const FLOOR_NEAR = 1.0;
// Depth is FLOOR_NEAR/t, so t must never reach zero. This caps apparent depth
// at ~167 units, well past where the haze has already taken everything.
const T_MIN = 0.006;
// ~4.9 tiles across the bottom edge at 16:9.
const TILE_FREQ = 2.2;
// Grout half-width in tile units, before the screen footprint is added.
const GROUT_W = 0.035;
// Water absorption. Nothing is hazed within HAZE_NEAR of the camera; past it
// the floor fades into the deep colour, reaching ~90% at 8 units and ~97% at
// 12. This is also what hides the far floor's aliasing, which is why the
// caustic depth cap below can be as tight as it is.
const HAZE_NEAR = 1.0;
const HAZE_K = 0.38;
// Caustic cells run a little smaller than a tile at mid distance.
export const CAUSTIC_FREQ = 0.45;
// Past this apparent depth the caustic domain stops growing. Two reasons: the
// noise would be sampled far below one cell per pixel (a shimmering buzz no
// amount of care fixes without derivatives), and the domain coordinate would
// grow past what a half-float can resolve on a device without highp fragment
// precision - see the CDOM macro. Haze is already ~0.98 here, so what freezes
// is invisible.
const CAUSTIC_DEPTH_CAP = 10.0;
// How fast a caustic line is widened once it falls below one pixel - see its
// use in buildFragmentShader. Purely a sampling correction; it does nothing at
// all in the near field, where cfoot is ~1e-3.
const CAUSTIC_SOFTEN = 260.0;
// The floor seen THROUGH moving water is displaced. Deliberately not derived
// from the caustic field: these are two different physical effects (bulk
// refraction of the view ray vs. focusing of the sun's rays), and tying them
// together makes the tiles visibly breathe in step with the light, which
// instantly reads as one animated texture rather than as water.
const WOBBLE = 0.035;

// --- caustic rendering -------------------------------------------------------
const CAUSTIC_GAIN = 1.15;
// Spread of focal length across the visible spectrum, as a fraction of d.
// This is the whole chromatic-fringe effect: red refracts least and so focuses
// slightly further than green, blue slightly nearer, and each channel's fold
// therefore sits a little to one side of the others. Small on purpose - much
// past this it stops reading as dispersion and starts reading as three
// separate coloured nets laid over each other.
const DISPERSE = 0.035;
// How fast a line falls off away from its fold, before the exponent. Together
// with the exponent this sets the line width; the exponent is the dial to
// reach for first (?expo=<n>), this is the coarse setting behind it.
const LINE_SHARPNESS = 1.55;
// The focal distance d, i.e. how far the floor sits below the surface in the
// field's own units. This is what bands.mid moves: pushing the floor further
// from the surface moves every fold, so the net visibly gathers and spreads.
const CONVERGENCE_BASE = 0.22;
// bands.mid swings convergence by this fraction, times the dynamics-derived
// amplitude below. The net tightens and loosens; it never blinks.
const CONVERGENCE_SWING = 0.42;
const DYNAMICS_AMPLITUDE_MIN = 0.6;
const DYNAMICS_AMPLITUDE_MAX = 1.3;
const DEFAULT_EXPONENT = 8;

// --- the surface band --------------------------------------------------------
const SUN_X_BASE = 0.62;      // off-centre, per the brief
const SUN_X_AMP = 0.16;
const SUN_X_RATE = 0.004;     // cycles per second - one sweep per ~4 minutes
// Sun elevation from progress: grazing at the start and end of a track,
// near-overhead in the middle. Feeds both the sun's height in the band and
// the caustics' obliquity, so the two stay consistent (acceptance: "sa
// position doit etre coherente avec l'orientation des caustiques").
const SUN_ELEV_MIN = 0.15;
const SUN_ELEV_RANGE = 0.75;
// How much the caustic domain is squashed along the depth axis at grazing
// sun, and how far it shears toward the sun's azimuth.
const SQUASH_GRAZING = 0.42;
const SKEW_MAX = 0.85;

// --- particles ---------------------------------------------------------------
const PARTICLE_RISE = 0.017;  // uv per second, times a 0.6..1.4 per-particle factor
const PARTICLE_SWAY = 0.020;
const PARTICLE_SIZE_MIN = 2.2;
const PARTICLE_SIZE_MAX = 5.0;
const PARTICLE_GAIN_MIN = 0.22;
const PARTICLE_GAIN_MAX = 0.62;

// --- reactivity --------------------------------------------------------------
// bands.low drives the drift RATE of the noise field, +/-18%. Note this is
// applied to a separately integrated clock (_fieldTime), never as a multiplier
// on a shared uTime - multiplying elapsed time by a changing rate moves the
// field backwards the instant the rate drops, which is a visible lurch.
const DRIFT_SWING = 0.18;
const TAU_DRIFT = 0.50;       // seconds; band smoothing time constants
const TAU_CONVERGENCE = 0.35;
const TAU_PARTICLES = 0.20;

// --- colour ------------------------------------------------------------------
// A red cover must give turquoise water with a rose cast, not a red pool
// (acceptance criterion 4).
//
// The brief's phrasing for this is "pulled 60% back toward cyan", and the
// literal reading - lerp(dominant, cyan, 0.6) - fails it in the other
// direction: red and cyan are complementary, so the straight line between
// them runs through grey, and a red cover came out as a grey-blue pool with no
// turquoise left in it. What that phrasing is protecting is the aquatic
// register, so the register is the fixed part here and the cover is mixed IN
// as a tint rather than being mixed OUT: a small fraction of the dominant,
// normalised to unit brightness first so a dark cover tints instead of just
// darkening. A red cover lifts the red channel from 0.01 to 0.16 against an
// unmoved green and blue - visibly rosy, unmistakably still turquoise.
const CYAN_ANCHOR = 0x1fb6c9;
const DEEP_ANCHOR = 0x062a4a;
// How much of the cover the water can take, at most - reached only by a cover
// that is already aquatic. A cover pointing AWAY from cyan is held down to
// COVER_INFLECTION_FLOOR of this (see _applyPalette): mixing a complementary
// hue in at full strength does not make the water rosy, it makes it grey, and
// then mauve. Rendered against a saturated red cover, 0.16 is where the water
// still reads unmistakably turquoise with a warm cast and 0.30 is already
// grey-mauve - so the floor is set to land a hostile cover exactly on 0.16,
// and everything friendlier gets more of a say than a single fixed constant
// could have given it.
const COVER_INFLECTION = 0.42;
const COVER_INFLECTION_FLOOR = 0.38;
const FALLBACK_DOMINANT = 0x2f7f9e;
const FALLBACK_VIVID = 0x8fe8ff;
// Everything that is NOT a caustic line is held below the pipeline's 0.78
// bloom threshold by construction, which is what makes acceptance criterion 5
// ("bloom without the image going white, including on a very bright cover")
// a property of the code rather than of the cover. A bright cover raises the
// water's HUE, never its luminance.
const TILE_MAX_LUMA = 0.24;
const DEEP_MAX_LUMA = 0.11;
const GROUT_MUL = 0.62;
const TILE_WHITE_MIX = 0.30;
const CAUSTIC_TINT_MIX = 0.16;
const SURFACE_TINT_MIX = 0.10;
const SURFACE_BASE = 0xfff4e2;
// Final ceiling on any pixel of the quad. The lines must clear 0.78 to bloom
// and must not reach 1.0, so they are scaled by luminance (which keeps the
// fringe hue) rather than clamped per channel (which would turn every line
// white and delete criterion 2 exactly where it is most visible).
const LUMA_CAP = 0.95;
const COLOR_LERP_MS = 900;

// The safe zone attenuates the LIGHT (caustics, glints, particles) by 45% and
// leaves the water and tiles alone - so the cover sits on a patch of calm
// water rather than on a dark hole. This file's own floor, per the brief; the
// shared chunk's 0.32 is for visualizers that dim everything.
const SAFE_LIGHT_FLOOR = 0.55;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- shader construction -----------------------------------------------------

// Precision of the DOMAIN coordinates only - the floor position, the noise
// lattice, the cell index. Everything else stays mediump per the performance
// contract. Apparent depth reaches ~14 units and the drift term grows without
// bound over a track, so these coordinates run to a few tens; a half-float
// resolves ~0.03 there, against a noise cell of 1.0, which would visibly
// quantise the lattice. Fragment highp is optional in GLSL ES 1.00, hence the
// guard: on a device without it this degrades to mediump rather than failing
// to compile, and the CAUSTIC_DEPTH_CAP above keeps that case tolerable.
export const PRECISION_PROLOGUE = `
  precision mediump float;
  #ifdef GL_FRAGMENT_PRECISION_HIGH
    #define CDOM highp
  #else
    #define CDOM mediump
  #endif
`;

// Dave Hoskins' hash. Not the one in shaders/inkField.glsl.js: that chunk is
// an ink-profile helper whose noise only has to make a blot's contour
// irregular, and its hash has enough channel correlation to leave faint
// diagonal streaks - invisible under a stain, and exactly the "grid" failure
// mode in a field read through its gradient.
export const HASH_CHUNK = `
  float hash21(CDOM vec2 p) {
    CDOM vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
`;

// One octave of value noise, twice-differentiated analytically.
//
// In the octave's own rotated and scaled frame q = freq * R * p + drift * t,
// with cell fraction f and corner values n00/n10/n01/n11:
//
//   n(f)      = n00 + k0*u(fx) + k1*u(fy) + k2*u(fx)*u(fy)
//   d2n/dfx2  = (k0 + k2*u(fy)) * u''(fx)
//   d2n/dfxfy = k2 * u'(fx) * u'(fy)
//   d2n/dfy2  = (k1 + k2*u(fx)) * u''(fy)
//
// u is the QUINTIC smoothstep, not the cubic one shaders/inkField.glsl.js
// uses. A cubic's second derivative is DISCONTINUOUS across a lattice
// boundary, and the second derivative is the entire signal here - with a cubic
// the fold jumps at every cell edge and draws the lattice for you, which is
// acceptance criterion 1's forbidden grid arriving through the back door.
//
// Back to p-space by the chain rule: H_p = freq^2 * R^T * H_q * R. The
// rotation is folded into the baked constants below, so no matrix is ever
// constructed at runtime and the whole transform costs six multiply-adds.
//
// Neither h nor its gradient is accumulated. Nothing reads either one.
function octaveChunk(o) {
  const c = Math.cos(o.rot);
  const s = Math.sin(o.rot);
  const af2 = o.amp * o.freq * o.freq;
  const f6 = (n) => n.toFixed(6);
  return `
    {
      CDOM vec2 q = vec2(${f6(c)} * p.x - ${f6(s)} * p.y,
                         ${f6(s)} * p.x + ${f6(c)} * p.y) * ${o.freq.toFixed(4)}
                    + vec2(${f6(o.drift[0])}, ${f6(o.drift[1])}) * uFieldTime;
      CDOM vec2 cell = floor(q);
      vec2 f = vec2(q - cell);
      float n00 = hash21(cell);
      float n10 = hash21(cell + vec2(1.0, 0.0));
      float n01 = hash21(cell + vec2(0.0, 1.0));
      float n11 = hash21(cell + vec2(1.0, 1.0));
      float k0 = n10 - n00;
      float k1 = n01 - n00;
      float k2 = n00 - n10 - n01 + n11;
      vec2 u  = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
      vec2 du = 30.0 * f * f * (f * (f - 2.0) + 1.0);
      float ex = k0 + k2 * u.y;
      float ey = k1 + k2 * u.x;
      vec2 ddu = 60.0 * f * (f * (2.0 * f - 3.0) + 1.0);
      float hxx = ex * ddu.x;
      float hxy = k2 * du.x * du.y;
      float hyy = ey * ddu.y;
      H += ${f6(af2)} * vec3(
        hxx * ${f6(c * c)} + 2.0 * hxy * ${f6(c * s)} + hyy * ${f6(s * s)},
        (hyy - hxx) * ${f6(c * s)} + hxy * ${f6(c * c - s * s)},
        hxx * ${f6(s * s)} - 2.0 * hxy * ${f6(c * s)} + hyy * ${f6(c * c)});
    }`;
}

export function buildFieldChunk(name, octaveCount) {
  const used = OCTAVES.slice(0, octaveCount);
  return `
  void ${name}(CDOM vec2 p, out vec3 H) {
    H = vec3(0.0);
${used.map(function (o) { return octaveChunk(o); }).join('\n')}
  }`;
}

// The exponentiation that turns a soft bump into a thin line. Unrolled
// squarings, never pow() with a variable exponent - and the exponent, not
// uConvergence, is the dial to reach for when the net comes out too thick
// (?expo=<n>).
function buildSharpenChunk(exponent) {
  const k = Math.log2(exponent);
  if (Number.isInteger(k) && k >= 1 && k <= 10) {
    let body = '';
    for (let i = 0; i < k; i++) body += '    c = c * c;\n';
    return `
  float sharpen(float c) {
${body}    return c;
  }`;
  }
  // Debug-only path (?expo= with a non-power-of-two). pow() with a uniform
  // exponent is what the performance contract bans; this one is a compile-
  // time literal, and it only ever exists in a hand-typed URL.
  return `
  float sharpen(float c) { return pow(c, ${exponent.toFixed(3)}); }`;
}

const SAFE_LIGHT_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  // Attenuates LIGHT only, to ${SAFE_LIGHT_FLOOR.toFixed(2)} behind the cover.
  // The water and the tiles keep their full brightness, so what appears under
  // the artwork is a patch of calm water, not a shadow.
  float safeLight(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_LIGHT_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

const vertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    // Fullscreen quad in clip space. The pool is a fixed pane; the scene's
    // slow camera drift would otherwise slide the whole body of water.
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

function buildFragmentShader(opts) {
  const octaveCount = opts.octaves;
  const disp = opts.dispersion;
  // See curvatureScale: keeps the same uConvergence meaning the same thing
  // on every tier.
  const convScale = FULL_CURVATURE / curvatureScale(octaveCount);
  return `
${PRECISION_PROLOGUE}

  uniform float uTime;
  uniform float uFieldTime;
  uniform float uConvergence;
  uniform float uSunX;
  uniform float uSunElev;
  uniform float uSunSkew;
  uniform float uSunSquash;
  uniform float uPixelUV;
  uniform vec3 uWaterDeep;
  uniform vec3 uTileColor;
  uniform vec3 uGroutColor;
  uniform vec3 uCausticTint;
  uniform vec3 uSurfaceTint;

  varying vec2 vUv;

${SAFE_LIGHT_CHUNK}
${HASH_CHUNK}
${buildFieldChunk('causticField', octaveCount)}
${buildSharpenChunk(opts.exponent)}

  // Scales any colour whose brightest channel exceeds the ceiling, rather
  // than clamping each channel: clamping would drive every line to pure white
  // and erase the chromatic fringes precisely where they are strongest.
  vec3 capLuma(vec3 c) {
    float mx = max(c.r, max(c.g, c.b));
    return mx > ${LUMA_CAP.toFixed(2)} ? c * (${LUMA_CAP.toFixed(2)} / mx) : c;
  }

  // The surface, seen from underneath: a sun and its reflected streaks.
  vec3 surfaceLight(vec2 uv) {
    vec2 sunUv = vec2(uSunX, mix(${(HORIZON + 0.02).toFixed(3)}, 1.02, uSunElev));
    vec2 d = (uv - sunUv) * vec2(uAspect, 1.0);
    float r2 = dot(d, d);
    float core = exp(-r2 * 34.0);
    float halo = exp(-r2 * 5.0);
    // High frequency in y, low in x: each streak is a long horizontal band a
    // few pixels tall. Looking up at the surface from underneath compresses it
    // vertically toward the horizon and smears every reflection sideways, so
    // which of the two axes carries the high frequency IS the effect - the
    // first version had them the other way round and drew vertical bars.
    // y is warped by a slow function of x first, so the bands undulate instead
    // of ruling the frame into perfect parallels.
    float wy = uv.y + 0.014 * sin(uv.x * 7.3 + uTime * 0.11)
                    + 0.008 * sin(uv.x * 15.1 - uTime * 0.17);
    float a = sin(wy * 155.0 + uTime * 0.31) * 0.5 + 0.5;
    float b = sin(wy * 97.0 - uTime * 0.19 + uv.x * 2.3) * 0.5 + 0.5;
    float c = sin(uv.x * 6.0 - uTime * 0.07) * 0.5 + 0.5;
    float glint = a * b * (0.35 + 0.65 * c);
    glint *= glint;
    return uSurfaceTint * (core * 0.62 + halo * 0.20 + glint * (0.14 + 0.45 * halo));
  }

  void main() {
    vec2 uv = vUv;
    vec3 col = uWaterDeep;
    float light = safeLight(uv);

    CDOM float t = ${HORIZON.toFixed(3)} - uv.y;
    // Screen-coherent branch: whole scanlines take the same side of it, and
    // it buys the entire floor + caustic cost back over the top fifth.
    if (t > 0.0) {
      CDOM float depth = ${FLOOR_NEAR.toFixed(3)} / max(t, ${T_MIN.toFixed(4)});
      CDOM float xw = (uv.x - 0.5) * uAspect;
      CDOM vec2 floorP = vec2(xw * depth, depth);
      float haze = 1.0 - exp(-max(depth - ${HAZE_NEAR.toFixed(2)}, 0.0) * ${HAZE_K.toFixed(3)});
      // Both the grout and the caustic need a screen footprint, and both are
      // this same reciprocal. One divide, used twice.
      float invT2 = 1.0 / float(t * t);

      // --- the tiled floor, seen through moving water ---
      vec2 wob = vec2(
        sin(float(floorP.y) * 1.7 + uTime * 0.23) + 0.6 * sin(float(floorP.x) * 2.3 - uTime * 0.17),
        sin(float(floorP.x) * 1.9 - uTime * 0.19) + 0.6 * sin(float(floorP.y) * 2.7 + uTime * 0.13)
      ) * ${WOBBLE.toFixed(4)};
      CDOM vec2 tp = floorP * ${TILE_FREQ.toFixed(3)} + vec2(wob);
      CDOM vec2 tcell = floor(tp);
      vec2 tf = vec2(tp - tcell);
      vec2 te = min(tf, 1.0 - tf);
      float edge = min(te.x, te.y);
      // Screen footprint of one tile unit, analytically: depth goes as
      // FLOOR_NEAR/t, so d(depth)/d(uv.y) is FLOOR_NEAR/t^2. WebGL1 has no
      // derivative instructions (see vinyl-groove.js's own note), and this is
      // exact rather than an approximation of them.
      float foot = ${(TILE_FREQ * FLOOR_NEAR).toFixed(4)} * uPixelUV * invT2;
      float w = ${GROUT_W.toFixed(4)} + foot;
      float tile = smoothstep(w * 0.35, w * 1.25, edge);
      // Once the joint is thinner than a pixel it cannot be drawn, only
      // averaged. Fading it out is what a mipmap would do, and it keeps the
      // far floor pale instead of turning it into a dark buzz.
      tile = mix(tile, 1.0, clamp((foot - ${GROUT_W.toFixed(4)}) * 6.0, 0.0, 1.0));
      float tint = 0.88 + 0.24 * hash21(tcell + 17.0);
      col = mix(mix(uGroutColor, uTileColor, tile) * tint, uWaterDeep, haze);

      // --- the caustic net ---
      // The domain stops growing past CAUSTIC_DEPTH_CAP - see its comment.
      CDOM float cd = min(depth, ${CAUSTIC_DEPTH_CAP.toFixed(2)});
      CDOM vec2 cp = vec2(xw * cd + cd * uSunSkew, cd * uSunSquash) * ${CAUSTIC_FREQ.toFixed(4)};

      vec3 H;
      causticField(cp, H);
      // Rays refracted by the surface land on the floor displaced by -d*grad h,
      // so the map from surface to floor has Jacobian J = I - d*H, and the
      // light arriving per unit area goes as 1/|det J|. The caustic is the set
      // where det J vanishes: rays that left a whole patch of surface arrive on
      // one line.
      //
      // det J = 1 - d*tr(H) + d^2*det(H), two multiply-adds from the Hessian
      // that is already in hand.
      float trH = H.x + H.z;
      float detH = H.x * H.z - H.y * H.y;
      float d = uConvergence * ${convScale.toFixed(4)};
      float j = 1.0 + d * (d * detH - trH);
      // A fold thinner than a pixel cannot be drawn - it samples as a dashed
      // line, which is the one artefact that reads as "computer graphics"
      // rather than as water. Past that point the line is widened in FIELD
      // units at the same rate it shrinks on screen, so it stays continuous
      // and simply dims. Same analytic footprint the grout uses.
      float cfoot = ${(CAUSTIC_FREQ * FLOOR_NEAR).toFixed(4)} * uPixelUV * invT2;
      float sharpness = ${LINE_SHARPNESS.toFixed(3)} / (1.0 + cfoot * ${CAUSTIC_SOFTEN.toFixed(1)});
      float conv = 1.0 - abs(j) * sharpness;
${opts.raw ? `
      // ?causticraw=1 - conv before exponentiation. If there is no net
      // structure at THIS stage, no exponent will invent one.
      gl_FragColor = vec4(vec3(clamp(conv, 0.0, 1.0)), 1.0);
      return;
` : ''}
      conv = max(conv, 0.0);
${disp ? `
      // Chromatic dispersion, and this is where the fold form pays for itself
      // a second time: refraction is wavelength-dependent, which means each
      // colour has its own FOCAL LENGTH - literally its own d. Red bends least
      // and focuses furthest, so its fold sits slightly outside green's and
      // blue's sits slightly inside. Two more evaluations of the same
      // quadratic, no second look at the noise.
      float dr = d * ${(1 + DISPERSE).toFixed(4)};
      float db = d * ${(1 - DISPERSE).toFixed(4)};
      vec3 caustic = vec3(
        sharpen(max(1.0 - abs(1.0 + dr * (dr * detH - trH)) * sharpness, 0.0)),
        sharpen(conv),
        sharpen(max(1.0 - abs(1.0 + db * (db * detH - trH)) * sharpness, 0.0)));
` : `
      // Tier 0: one focal length, one white line, no fringes.
      vec3 caustic = vec3(sharpen(conv));
`}
      // Squared rather than linear in (1 - haze): a caustic line is far
      // brighter than the floor it sits on, so at the distance where the tile
      // has already dissolved the line is still a hard bright speck. It has to
      // be taken out faster than the surface it lights.
      float reach = 1.0 - haze;
      col += caustic * uCausticTint * (${CAUSTIC_GAIN.toFixed(3)} * light * reach * reach);
    }

    // The surface band. Weighted in rather than switched on, so the floor's
    // vanishing line and the underside of the water meet without a seam.
    float bandW = smoothstep(${(HORIZON - 0.03).toFixed(3)}, ${(HORIZON + 0.09).toFixed(3)}, uv.y);
    if (bandW > 0.0) {
      col += surfaceLight(uv) * (bandW * light);
    }

    gl_FragColor = vec4(capLuma(col), 1.0);
  }
`;
}

// The suspended motes. One THREE.Points, additive, no per-frame CPU work: the
// drift is a closed form of uFieldTime, so a pause freezes them for free along
// with everything else.
//
// Each mote reads the caustic field at its own position and brightens inside a
// strong zone - the brief's "they shine more when they cross a caustic". Two
// octaves rather than the floor's three: this runs 80 times per frame in
// total, so the cost is irrelevant, but the coarse octaves are what the eye
// actually matches between a mote and the net under it.
function buildParticleVertexShader() {
  const convScale = FULL_CURVATURE / curvatureScale(PARTICLE_OCTAVES);
  return `
  precision mediump float;
  #define CDOM highp

  attribute vec2 aBase;
  attribute vec3 aTraits;   // x rise rate, y phase seed, z size in px

  uniform float uFieldTime;
  uniform float uConvergence;
  uniform float uSunSkew;
  uniform float uSunSquash;
  uniform float uPointScale;
  uniform float uParticleGain;

  varying float vBright;

${SAFE_LIGHT_CHUNK}
${HASH_CHUNK}
${buildFieldChunk('particleField', PARTICLE_OCTAVES)}

  void main() {
    float y = fract(aBase.y + uFieldTime * aTraits.x * ${PARTICLE_RISE.toFixed(5)});
    float x = fract(aBase.x
      + sin(uFieldTime * 0.11 + aTraits.y) * ${PARTICLE_SWAY.toFixed(4)}
      + sin(uFieldTime * 0.043 + aTraits.y * 2.7) * ${(PARTICLE_SWAY * 0.6).toFixed(4)});
    vec2 uv = vec2(x, y);

    CDOM float t = max(${HORIZON.toFixed(3)} - uv.y, 0.02);
    CDOM float cd = min(${FLOOR_NEAR.toFixed(3)} / t, ${CAUSTIC_DEPTH_CAP.toFixed(2)});
    CDOM float xw = (uv.x - 0.5) * uAspect;
    CDOM vec2 cp = vec2(xw * cd + cd * uSunSkew, cd * uSunSquash) * ${CAUSTIC_FREQ.toFixed(4)};

    vec3 H;
    particleField(cp, H);
    float d = uConvergence * ${convScale.toFixed(4)};
    float j = 1.0 + d * (d * (H.x * H.z - H.y * H.y) - (H.x + H.z));
    // Half the floor's sharpness and a fourth power rather than an eighth: a
    // mote sits IN the beam, not at its focus, so the zone it lights up in is
    // wider than the line the beam finally draws on the tiles.
    float conv = max(1.0 - abs(j) * ${(LINE_SHARPNESS * 0.5).toFixed(3)}, 0.0);
    conv = conv * conv; conv = conv * conv;

    float fade = smoothstep(0.0, 0.06, y) * (1.0 - smoothstep(0.86, 0.99, y));
    vBright = (0.30 + 0.85 * conv) * uParticleGain * fade * safeLight(uv);

    gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0);
    gl_PointSize = aTraits.z * uPointScale;
  }
`;
}

const particleFragmentShader = `
  precision mediump float;
  uniform vec3 uParticleColor;
  varying float vBright;
  void main() {
    vec2 d = gl_PointCoord - vec2(0.5);
    float r2 = dot(d, d) * 4.0;
    if (r2 > 1.0) discard;
    float core = 1.0 - smoothstep(0.2, 1.0, r2);
    gl_FragColor = vec4(uParticleColor * core * vBright, 1.0);
  }
`;

// --- colour helpers ----------------------------------------------------------

const LUMA = { r: 0.2126, g: 0.7152, b: 0.0722 };

// Built once. _writeColors runs every frame of a 900ms palette crossfade, and
// a THREE.Color constructor there would be ~50 allocations per track change
// for two values that never vary.
const CYAN = new THREE.Color(CYAN_ANCHOR);
const DEEP = new THREE.Color(DEEP_ANCHOR);
const WHITE = new THREE.Color(0xffffff);

function capLuminance(color, maxLuma) {
  const l = LUMA.r * color.r + LUMA.g * color.g + LUMA.b * color.b;
  if (l > maxLuma && l > 0) color.multiplyScalar(maxLuma / l);
  return color;
}

// Cosine similarity between the cover's chroma and the anchor's, remapped to
// [COVER_INFLECTION_FLOOR, 1]. Chroma is the colour minus its own grey level,
// so this asks "which way does this cover point, ignoring how light it is" -
// a grey cover has no direction and lands in the middle. Runs once per track
// change, never per frame.
function coverAffinity(cover) {
  const cm = (cover.r + cover.g + cover.b) / 3;
  const am = (CYAN.r + CYAN.g + CYAN.b) / 3;
  const cx = cover.r - cm, cy = cover.g - cm, cz = cover.b - cm;
  const ax = CYAN.r - am, ay = CYAN.g - am, az = CYAN.b - am;
  const cl = Math.sqrt(cx * cx + cy * cy + cz * cz);
  const al = Math.sqrt(ax * ax + ay * ay + az * az);
  const sim = cl > 1e-4 && al > 1e-4 ? (cx * ax + cy * ay + cz * az) / (cl * al) : 0;
  return COVER_INFLECTION_FLOOR
    + (1 - COVER_INFLECTION_FLOOR) * (0.5 + 0.5 * sim);
}

function approach(current, target, deltaTime, tau) {
  return current + (target - current) * (1 - Math.exp(-deltaTime / tau));
}

export class CausticsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : 1;
    this._tier = TIER_OCTAVES[tier] != null ? tier : 1;
    this._raw = urlParam('causticraw') === '1';
    const expo = parseFloat(urlParam('expo'));
    this._exponent = expo > 1 && expo <= 512 ? expo : DEFAULT_EXPONENT;
    this._noDispersion = urlParam('nodisp') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    const dispersion = TIER_DISPERSION[this._tier] && !this._noDispersion;

    // One set of holder objects shared by both materials - Three.js is happy
    // for two ShaderMaterials to point at the same uniform objects, so the
    // field clock, convergence and sun geometry are written once per frame
    // and both the floor and the motes read the same values. That sharing is
    // not an optimisation, it is what keeps the motes lit by the same field
    // the floor is drawing.
    this._shared = {
      uFieldTime: { value: 0 },
      uConvergence: { value: CONVERGENCE_BASE },
      uSunSkew: { value: 0 },
      uSunSquash: { value: 1 }
    };

    this._quadUniforms = Object.assign({}, safeZoneUniforms, this._shared, {
      uTime: { value: 0 },
      uSunX: { value: SUN_X_BASE },
      uSunElev: { value: 0.5 },
      uPixelUV: { value: 1 / 1080 },
      uWaterDeep: { value: new THREE.Color(0x06263f) },
      uTileColor: { value: new THREE.Color(0xbfd8dd) },
      uGroutColor: { value: new THREE.Color(0x4f6a72) },
      uCausticTint: { value: new THREE.Color(0xffffff) },
      uSurfaceTint: { value: new THREE.Color(SURFACE_BASE) }
    });

    this._geometry = new THREE.PlaneGeometry(1, 1);
    this._material = new THREE.ShaderMaterial({
      uniforms: this._quadUniforms,
      vertexShader: vertexShader,
      fragmentShader: buildFragmentShader({
        octaves: TIER_OCTAVES[this._tier],
        dispersion: dispersion,
        exponent: this._exponent,
        raw: this._raw
      }),
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._mesh = new THREE.Mesh(this._geometry, this._material);
    this._mesh.frustumCulled = false;
    this._mesh.renderOrder = -1000;
    scene.add(this._mesh);

    this._buildParticles(scene);

    this._time = 0;
    this._fieldTime = 0;
    this._driftRate = 1;
    this._convergence = CONVERGENCE_BASE;
    this._particleGain = PARTICLE_GAIN_MIN;
    this._lastPaletteRef = null;
    this._colorLerp = 1;
    this._waterFrom = new THREE.Color(FALLBACK_DOMINANT);
    this._waterTo = new THREE.Color(FALLBACK_DOMINANT);
    this._vividFrom = new THREE.Color(FALLBACK_VIVID);
    this._vividTo = new THREE.Color(FALLBACK_VIVID);
    this._water = new THREE.Color();
    this._scratch = new THREE.Color();
    this._vivid = new THREE.Color();
    // Painted immediately rather than left to the first palette change: a
    // track with no cover art never produces one, and the placeholder uniform
    // values above would otherwise be what shipped.
    this._applyPalette(FALLBACK_DOMINANT, FALLBACK_VIVID);
    this._waterFrom.copy(this._waterTo);
    this._vividFrom.copy(this._vividTo);
    this._water.copy(this._waterTo);
    this._vivid.copy(this._vividTo);
    this._writeColors();
  }

  _buildParticles(scene) {
    const count = TIER_PARTICLES[this._tier];
    this._points = null;
    if (count <= 0) return;

    const base = new Float32Array(count * 2);
    const traits = new Float32Array(count * 3);
    // Positions are never read as geometry (the vertex shader builds clip
    // space from aBase alone), but Three.js still needs a `position`
    // attribute to know the draw count.
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      base[i * 2] = Math.random();
      base[i * 2 + 1] = Math.random();
      traits[i * 3] = 0.6 + Math.random() * 0.8;
      traits[i * 3 + 1] = Math.random() * 6.2832;
      traits[i * 3 + 2] = PARTICLE_SIZE_MIN + Math.random() * (PARTICLE_SIZE_MAX - PARTICLE_SIZE_MIN);
    }

    this._particleGeometry = new THREE.BufferGeometry();
    this._particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this._particleGeometry.setAttribute('aBase', new THREE.BufferAttribute(base, 2));
    this._particleGeometry.setAttribute('aTraits', new THREE.BufferAttribute(traits, 3));
    this._particleGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this._particleUniforms = Object.assign({}, safeZoneUniforms, this._shared, {
      uPointScale: { value: 1 },
      uParticleGain: { value: PARTICLE_GAIN_MIN },
      uParticleColor: { value: new THREE.Color(0xffffff) }
    });

    this._particleMaterial = new THREE.ShaderMaterial({
      uniforms: this._particleUniforms,
      vertexShader: buildParticleVertexShader(),
      fragmentShader: particleFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      depthWrite: false
    });

    this._points = new THREE.Points(this._particleGeometry, this._particleMaterial);
    this._points.frustumCulled = false;
    this._points.renderOrder = 10;
    scene.add(this._points);
  }

  // dominant/vivid as hex or THREE.Color. Everything downstream of the water
  // colour is derived here, so there is exactly one place where a cover can
  // influence the image and exactly one place where that influence is bounded.
  _applyPalette(dominant, vivid) {
    // Normalised to unit maximum first - see COVER_INFLECTION. Without this a
    // near-black dominant would subtract brightness from the anchor instead of
    // colouring it.
    const cover = this._scratch.set(dominant);
    const mx = Math.max(cover.r, Math.max(cover.g, cover.b));
    if (mx > 1e-4) cover.multiplyScalar(1 / mx);
    const m = COVER_INFLECTION * coverAffinity(cover);
    const k = 1 - m;
    this._waterTo.setRGB(
      CYAN.r * k + cover.r * m,
      CYAN.g * k + cover.g * m,
      CYAN.b * k + cover.b * m);
    this._vividTo.set(vivid);
  }

  _writeColors() {
    const u = this._quadUniforms;
    const water = this._water;
    const vivid = this._vivid;

    u.uTileColor.value.copy(WHITE).lerp(water, TILE_WHITE_MIX);
    capLuminance(u.uTileColor.value, TILE_MAX_LUMA);
    u.uGroutColor.value.copy(u.uTileColor.value).multiplyScalar(GROUT_MUL);

    u.uWaterDeep.value.copy(water).lerp(DEEP, 0.55);
    capLuminance(u.uWaterDeep.value, DEEP_MAX_LUMA);

    // Near white with a trace of the cover in it. The fringes get their own
    // colours from the three-channel evaluation, not from here - this only
    // decides what "white" means for this track.
    u.uCausticTint.value.copy(WHITE).lerp(vivid, CAUSTIC_TINT_MIX);
    u.uSurfaceTint.value.set(SURFACE_BASE).lerp(vivid, SURFACE_TINT_MIX);

    if (this._particleUniforms) {
      this._particleUniforms.uParticleColor.value.copy(WHITE).lerp(water, 0.15);
    }
  }

  update(deltaTime, profile) {
    // A pool does not move when the music stops. Both clocks freeze, so the
    // field, the motes and the surface all hold exactly where they were.
    if (profile.playbackState !== 'PLAYING') return;

    const director = profile.director;
    const bands = director && director.bands;
    const character = director && director.character;
    const dynamics = character && character.dynamics != null ? character.dynamics : 0.5;

    this._time += deltaTime;

    // bands.low -> drift RATE, +/-18%, integrated rather than multiplied in
    // (see DRIFT_SWING).
    const low = bands && bands.low != null ? bands.low : 0.5;
    this._driftRate = approach(
      this._driftRate, 1 + (low - 0.5) * 2 * DRIFT_SWING, deltaTime, TAU_DRIFT);
    this._fieldTime += deltaTime * this._driftRate;

    // bands.mid -> convergence: the net tightens and loosens. The amplitude of
    // that swing is set by the track's dynamics, so a flat mix breathes less
    // than a dynamic one rather than both swinging the same amount.
    const mid = bands && bands.mid != null ? bands.mid : 0.5;
    const amplitude = DYNAMICS_AMPLITUDE_MIN
      + (DYNAMICS_AMPLITUDE_MAX - DYNAMICS_AMPLITUDE_MIN) * dynamics;
    const convTarget = CONVERGENCE_BASE * (1 + (mid - 0.5) * 2 * CONVERGENCE_SWING * amplitude);
    this._convergence = approach(this._convergence, convTarget, deltaTime, TAU_CONVERGENCE);

    // bands.high -> how much the suspended motes catch the light.
    const high = bands && bands.high != null ? bands.high : 0.4;
    this._particleGain = approach(
      this._particleGain,
      PARTICLE_GAIN_MIN + (PARTICLE_GAIN_MAX - PARTICLE_GAIN_MIN) * high,
      deltaTime, TAU_PARTICLES);

    // progress -> sun height, and through it the obliquity of the net:
    // grazing at the start and end of a track, closer to overhead in the
    // middle. The same value places the sun in the surface band, so the
    // light's direction and the caustics' lean always agree.
    const progress = profile.duration > 0
      ? THREE.MathUtils.clamp(profile.progress, 0, 1)
      : 0.5;
    const elev = SUN_ELEV_MIN + SUN_ELEV_RANGE * Math.sin(Math.PI * progress);
    const sunX = SUN_X_BASE + SUN_X_AMP * Math.sin(this._time * SUN_X_RATE * 6.2832);

    const u = this._quadUniforms;
    u.uTime.value = this._time;
    u.uSunElev.value = elev;
    u.uSunX.value = sunX;
    this._shared.uFieldTime.value = this._fieldTime;
    this._shared.uConvergence.value = this._convergence;
    this._shared.uSunSquash.value = SQUASH_GRAZING + (1 - SQUASH_GRAZING) * elev;
    this._shared.uSunSkew.value = (sunX - 0.5) * SKEW_MAX * (1 - elev);

    // Same canvas-height convention every other file here uses (see
    // clave.js): the composer renders at a fraction of this,
    // so it is an estimate, but the only thing riding on it is how many
    // pixels wide the grout's antialiasing ramp is.
    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    u.uPixelUV.value = canvasPixelHeight > 0 ? 1 / canvasPixelHeight : 1 / 1080;
    if (this._particleUniforms) {
      this._particleUniforms.uPointScale.value = Math.max(0.5, canvasPixelHeight / 1080);
      this._particleUniforms.uParticleGain.value = this._particleGain;
    }

    const palette = profile.artworkPalette;
    if (palette !== this._lastPaletteRef) {
      this._lastPaletteRef = palette;
      const dominant = palette && palette.dominantColors && palette.dominantColors.length
        ? palette.dominantColors[0] : FALLBACK_DOMINANT;
      const vivid = palette && palette.vividColors && palette.vividColors.length
        ? palette.vividColors[0] : FALLBACK_VIVID;
      this._waterFrom.copy(this._water);
      this._vividFrom.copy(this._vivid);
      this._applyPalette(dominant, vivid);
      this._colorLerp = 0;
    }
    if (this._colorLerp < 1) {
      this._colorLerp = Math.min(1, this._colorLerp + deltaTime * 1000 / COLOR_LERP_MS);
      this._water.copy(this._waterFrom).lerp(this._waterTo, this._colorLerp);
      this._vivid.copy(this._vividFrom).lerp(this._vividTo, this._colorLerp);
      this._writeColors();
    }
  }

  dispose() {
    this._scene.remove(this._mesh);
    this._geometry.dispose();
    this._material.dispose();
    if (this._points) {
      this._scene.remove(this._points);
      this._particleGeometry.dispose();
      this._particleMaterial.dispose();
      this._points = null;
    }
  }
}
